import type { EstimateVisibility } from '../interfaces/estimate/estimate.interface';

/**
 * Mirrors ProjulV1Portal's src/app/shared/Utils/estimate-visibility.ts, minus its
 * `seedEstimateVisibility`: this app only ever reads the bag, it never creates an estimate.
 */

/**
 * Every client-facing estimate field is visible unless someone turned it off, so a flag a stored
 * bag was written before can only ever resolve to shown.
 */
export const DEFAULT_ESTIMATE_VISIBILITY: EstimateVisibility = {
  ShowCompanySection: true,
  ShowCompanyLogo: true,
  ShowCompanyName: true,
  ShowLicenseNumber: true,
  ShowCompanyAddress: true,
  ShowCompanyPhone: true,
  ShowCompanyEmail: true,
  ShowCompanyWebsite: true,

  ShowClientSection: true,
  ShowClientName: true,
  ShowClientAddress: true,
  ShowClientPhone: true,
  ShowClientEmail: true,

  ShowProjectName: true,
  ShowProjectNumber: true,
  ShowProjectAddress: true,
  ShowProjectPhone: true,

  ShowDocumentTitle: true,
  ShowDocumentNumber: true,
  ShowDocumentDate: true,
  ShowExpiryDate: true,
  ShowCustomFields: true,

  ShowIntroduction: true,
  ShowAttachments: true,
  ShowContractDetails: true,
  ShowSignature: true,
};

const VISIBILITY_KEYS = Object.keys(DEFAULT_ESTIMATE_VISIBILITY) as (keyof EstimateVisibility)[];

/** What the wire can actually hold: any flag may be absent or null on a bag stored before it existed. */
export type StoredEstimateVisibility = { [K in keyof EstimateVisibility]?: boolean | null };

/**
 * Resolves ONE bag against the defaults; `resolveDocumentHeader` picks which bag a document uses.
 * Per-key, never a spread, because a spread keeps an explicit `null` and `null` reads as "hide".
 */
export function resolveEstimateVisibility(
  estimate: { Visibility?: StoredEstimateVisibility | null } | null | undefined
): EstimateVisibility {
  const bag = estimate?.Visibility;
  const resolved = {} as EstimateVisibility;
  for (const key of VISIBILITY_KEYS) {
    resolved[key] = bag?.[key] ?? DEFAULT_ESTIMATE_VISIBILITY[key];
  }
  return resolved;
}
